import { OrbitalTrackRentalPopupType } from '@apeiron/orbital'
import { PlanetLite } from '@apeiron/planet'
import { StarLite } from '@apeiron/star'
import { styled, useMediaQuery } from '@mui/material'
import OrbitalTrackOperatingStarInfo from '@src/components/orbitalTrack/detachRenew/OrbitalTrackOperatingStarInfo'
import OrbitingPlanetPlanetCard from '@src/components/orbitalTrack/detachRenew/OrbitingPlanetPlanetCard'
import OrbitingPlanetSummary from '@src/components/orbitalTrack/detachRenew/OrbitingPlanetSummary'
import RentalConfirmSection from '@src/components/orbitalTrack/detachRenew/RentalConfirmSection'
import RentalTermsSection from '@src/components/orbitalTrack/detachRenew/RentalTermsSection'
import ApeironScrollBox from '@src/components/share/apeiron/ApeironScrollBox'
import { useStarHelper } from '@src/hooks/apeiron/star/useStarHelper'
import theme from '@src/styles/theme'
import {
  OrbitalTrackDetachDetail,
  OrbitalTrackLite,
  OrbitalTrackSetting
} from '@src/types/orbitalTrack'
import * as R from 'ramda'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(ApeironScrollBox)`
  color: white;
  overflow-y: auto;
  padding-top: 40px;
  width: 100%;
`

const SingleRentalView: FC<Props> = props => {
  const {
    className,
    hideConfirmButton = false,
    planet,
    popupType,
    rentalDetail,
    setting,
    star,
    tracks,
    onConfirm,
    onSuccess
  } = props

  const { isOwner } = useStarHelper(star)

  const { t } = useTranslation()

  const mobile = useMediaQuery(theme.breakpoints.down('res768'))

  const detailTitle = () => {
    switch (popupType) {
      case OrbitalTrackRentalPopupType.Planet_Owner:
        return t('godiverse.orbital_track.rental_popup.summary.title_planet')
      case OrbitalTrackRentalPopupType.Star_Owner:
        return t('godiverse.orbital_track.rental_popup.summary.title_star')
      case OrbitalTrackRentalPopupType.Renew:
        return t('godiverse.orbital_track.rental_popup.summary.title_renew')
    }
  }

  const desktopSubtitle =
    popupType === OrbitalTrackRentalPopupType.Renew
      ? t('godiverse.orbital_track.rental_popup.summary.subtitle_renew')
      : t('godiverse.orbital_track.rental_popup.summary.subtitle_end', {
          planetName: rentalDetail?.detachPlanet?.name
        })

  const mobileSubtitle = t(
    'godiverse.orbital_track.attach_planet.rental_details'
  )

  const loading = isOwner
    ? R.isNil(star) || R.isNil(setting)
    : R.isNil(star) || R.isNil(rentalDetail)

  return (
    <Container className={className}>
      <OrbitalTrackOperatingStarInfo
        isOwner={isOwner}
        loading={loading}
        setting={setting}
        settingOnRenting={rentalDetail}
        star={star}
        isRenew={popupType === OrbitalTrackRentalPopupType.Renew}
        subtitle={mobile ? mobileSubtitle : desktopSubtitle}
        title={detailTitle() || ''}
      />
      {!R.isNil(rentalDetail) && (
        <OrbitingPlanetSummary
          popupType={popupType}
          rentalDetail={rentalDetail}
        />
      )}
      {!R.isNil(planet) && <OrbitingPlanetPlanetCard planet={planet} />}
      {!hideConfirmButton && (
        <>
          <RentalTermsSection popupType={popupType} />
          <RentalConfirmSection
            popupType={popupType}
            tracks={tracks}
            onConfirm={onConfirm}
            onSuccess={onSuccess}
          />
        </>
      )}
    </Container>
  )
}

type Props = {
  className?: string
  hideConfirmButton?: boolean
  planet: PlanetLite | null
  popupType: OrbitalTrackRentalPopupType
  rentalDetail?: OrbitalTrackDetachDetail | null
  setting?: OrbitalTrackSetting | null
  star: StarLite | null
  tracks?: OrbitalTrackLite[]
  onConfirm?: (successIds: string[]) => void
  onSuccess?: (successIds: string[]) => void
}

export default SingleRentalView
